import React, { useEffect, useState, useMemo, useRef } from 'react';
import AppConst from "../apis/appconst";
import axios from "axios";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

const MlcRegistrationList = () => {

  const navigate = useNavigate();

  const [headerLogo, setHeaderLogo] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [ldata, setLdata] = useState(null);
  const [dropdownOpenIndex, setDropdownOpenIndex] = useState(null);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    l1: [],
    docs: [],
    cats: [],
    from: "",
    to: "",
    search_key: "",
    page_no: 0,
  });

  const filtersRef = useRef({ from: '', to: '', search_key: '' });

  /* ============================================================
   *  HELPERS
   * ============================================================ */

  const getLocalYYYYMMDD = (d = new Date()) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const getStoredUser = () => {
    try {
      const s = sessionStorage.getItem("ldata");
      if (s) {
        try { return JSON.parse(s); } catch (e) { /* fallthrough */ }
      }
      const other = localStorage.getItem("data") || localStorage.getItem("ldata") || sessionStorage.getItem("data");
      if (other) {
        try { return JSON.parse(other); } catch (e) { /* fallthrough */ }
      }
    } catch (err) { /* ignore */ }
    return null;
  };

  // attach logged-in user info to any FormData
  const attachUserInfo = (fd) => {
    try {
      const u = getStoredUser();
      if (u) {
        fd.append('user_id', u.user_id || '');
        fd.append('user_name', `${u.fname || ''} ${u.lname || ''}`.trim());
        fd.append('user_role', u.role_id || u.role_name || '');
      }
    } catch (e) {
      console.warn("attachUserInfo failed:", e);
    }
    return fd;
  };

  // Robust local date parser (avoids UTC shifts on "2026-10-29 12:51:31")
  function parseServerDateToLocal(dateStr) {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return dateStr;
    const normalized = String(dateStr).trim().replace(' ', 'T');
    const m = normalized.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/);
    if (!m) return null;
    return new Date(
      Number(m[1]), Number(m[2]) - 1, Number(m[3]),
      Number(m[4] ?? 0), Number(m[5] ?? 0), Number(m[6] ?? 0)
    );
  }

  const formatDateLocal = (dateStr) => {
    const dt = parseServerDateToLocal(dateStr);
    if (!dt) return '';
    const dd = String(dt.getDate()).padStart(2, '0');
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${dd}-${months[dt.getMonth()]}-${dt.getFullYear()}`;
  };

  // 12-hour display: "29-Oct-2026 01:04 PM"
  const formatDateTimeLocal = (dateStr) => {
    const dt = parseServerDateToLocal(dateStr);
    if (!dt) return '';
    const dd = String(dt.getDate()).padStart(2, '0');
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    let h = dt.getHours();
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;                       // 0 -> 12 AM, 13 -> 1 PM
    const hh = String(h).padStart(2, '0');
    const mi = String(dt.getMinutes()).padStart(2, '0');
    return `${dd}-${months[dt.getMonth()]}-${dt.getFullYear()} ${hh}:${mi} ${ampm}`;
  };

  const getDoctorNameById = (id, row = null) => {
    if (row && row.mlc_doctor_name) return row.mlc_doctor_name;
    const doctor = (formData.docs || []).find(d => String(d.user_id) === String(id));
    if (doctor && doctor.fname) return doctor.fname;
    return id ? String(id) : '';
  };

  const getDepartmentNameById = (id, row = null) => {
    if (row && row.mlc_department_name) return row.mlc_department_name;
    const cat = (formData.cats || []).find(c => String(c.cat_id) === String(id));
    return cat ? cat.cat_name : (id ? String(id) : '');
  };

  const imageUrlToBase64 = async (url) => {
    if (!url) return null;
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      const blob = await response.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      return null;
    }
  };

  /* ============================================================
   *  FILTERS
   * ============================================================ */

  const updateFilters = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));

    if (updates.from !== undefined) filtersRef.current.from = updates.from;
    if (updates.to !== undefined) filtersRef.current.to = updates.to;
    if (updates.search_key !== undefined) filtersRef.current.search_key = updates.search_key;

    // Date change -> auto reload list from server
    if (updates.from !== undefined || updates.to !== undefined) {
      initData({
        from: updates.from !== undefined ? updates.from : filtersRef.current.from,
        to: updates.to !== undefined ? updates.to : filtersRef.current.to,
        search_key: filtersRef.current.search_key
      });
    }
  };

  const clearFilters = () => {
    const todayStr = getLocalYYYYMMDD();
    setFormData(prev => ({ ...prev, search_key: "", from: todayStr, to: todayStr, page_no: 0 }));
    filtersRef.current = { from: todayStr, to: todayStr, search_key: '' };
    initData({ from: todayStr, to: todayStr, search_key: '' });
  };

  /* ============================================================
   *  DATA FETCHERS
   * ============================================================ */

  const initData = async (overrides = {}) => {
    const current = filtersRef.current;
    const fromVal = overrides.from !== undefined ? overrides.from : current.from;
    const toVal = overrides.to !== undefined ? overrides.to : current.to;
    const searchKey = overrides.search_key !== undefined ? overrides.search_key : current.search_key;

    const fd = new FormData();
    fd.append('page_no', "none");           // DataTables handles paging client-side
    fd.append('search_key', searchKey || "");
    if (fromVal) fd.append('from', fromVal);
    if (toVal) fd.append('to', toVal);
    fd.append('status', '0');

    setLoading(true);
    try {
      const response = await axios.post(AppConst.BASE_URL + AppConst.getMlcListUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.status) {
        setFormData(prev => ({ ...prev, l1: response.data.records || [] }));
      } else {
        setFormData(prev => ({ ...prev, l1: [] }));
        console.warn('get_mlc_list: status false', response.data);
      }
    } catch (error) {
      console.error("MLC initData error:", error);
      if (error?.response) console.error('Server response:', error.response.data);
      toast.error("Could not load MLC list");
    } finally {
      setLoading(false);
    }
  };

  // doctors (used for name fallback)
  const initDoctors = async () => {
    const fd = new FormData();
    fd.append('type', "doctor");
    fd.append('search_key', "");
    fd.append('page_no', "none");
    fd.append('user_cre_by', "1");
    try {
      const response = await axios.post(AppConst.BASE_URL + AppConst.getUserListUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.status) {
        setFormData(prev => ({ ...prev, docs: response.data.records || [] }));
      }
    } catch (error) {
      console.error("initDoctors error:", error);
    }
  };

  // departments / specialities (used for name fallback)
  const initDepartments = async () => {
    try {
      const fd = new FormData();
      fd.append('cat_type', "speciality");
      fd.append('page_no', "none");
      fd.append('par_by', "1");
      const response = await axios.post(AppConst.BASE_URL + AppConst.getCatListUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.status) {
        setFormData(prev => ({ ...prev, cats: response.data.records || [] }));
      }
    } catch (err) { /* ignore */ }
  };

  const fetchConfigData = async () => {
    try {
      const res = await axios.post(`${AppConst.BASE_URL + AppConst.getconfig}?config_type=lab`);
      if (res.data.status) setHeaderLogo(res.data.header_logo);
    } catch (err) { /* ignore */ }
  };

  /* ============================================================
   *  LIFECYCLE
   * ============================================================ */

  useEffect(() => {
    const storedUser = getStoredUser();
    if (storedUser) {
      setLdata(storedUser);
      const maybeIsAdmin =
        storedUser.is_admin === 1 ||
        storedUser.isAdmin === 1 ||
        storedUser.role === 'admin' ||
        storedUser.user_type === 'admin' ||
        storedUser.role_id === 1 || storedUser.role_id === "1" ||
        storedUser.user_role === 'admin';
      setIsAdmin(Boolean(maybeIsAdmin));
    }

    const todayStr = getLocalYYYYMMDD();
    setFormData(prev => ({ ...prev, from: todayStr, to: todayStr }));
    filtersRef.current = { from: todayStr, to: todayStr, search_key: '' };

    initData({ from: todayStr, to: todayStr, search_key: '' });
    initDoctors();
    initDepartments();
    fetchConfigData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh when coming back from the registration page after a save
  useEffect(() => {
    try {
      if (sessionStorage.getItem('mlc_refresh') === '1') {
        sessionStorage.removeItem('mlc_refresh');
        initData();
      }
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ============================================================
   *  CLIENT-SIDE SEARCH (name / mlc no / uhid / mobile / FIR)
   * ============================================================ */

  const filteredList = useMemo(() => {
    const list = formData.l1 || [];
    const rawSearch = (formData.search_key || "").toString().toLowerCase().trim();
    if (!rawSearch) return list;

    return list.filter(item => {
      const candidates = [
        item.mlc_no,
        item.mlc_uhid,
        item.mlc_patient_name,
        item.mlc_attendant_name,
        item.mlc_mobile,
        item.mlc_fir_no,
        item.mlc_doctor_name,
        item.mlc_police_station,
      ].filter(Boolean).map(x => String(x).toLowerCase());

      return candidates.some(c => c.includes(rawSearch));
    });
  }, [formData.l1, formData.search_key]);

  /* ============================================================
   *  ADD / EDIT / DELETE
   * ============================================================ */

  const addRegistration = () => {
    try { sessionStorage.removeItem("mlc_dt"); } catch (e) { /* ignore */ }
    navigate('/mlc_registration');
  };

  const editRow = (row) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "Do you want to edit this MLC record?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Edit'
    }).then((result) => {
      if (result.isConfirmed) {
        sessionStorage.setItem("mlc_dt", JSON.stringify(row));
        navigate('/mlc_registration');
      }
    });
  };

  const delItem = async (row) => {
    const fd = new FormData();
    fd.append('mlc_id', row.mlc_id + "");
    attachUserInfo(fd);
    try {
      const response = await axios.post(AppConst.BASE_URL + AppConst.deleteMlcUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.status) {
        toast.success(response.data.success || "MLC Record Deleted Successfully");
        await initData();
      } else {
        toast.error(response.data.success || "Failed to delete MLC record");
      }
    } catch (error) {
      console.error("delItem error:", error);
      toast.error("Server error while deleting MLC record");
    }
  };

  const confirmDelete = (row) => {
    Swal.fire({
      title: 'Are you sure?',
      html: `This will remove MLC No. <b>${row.mlc_no || ''}</b> from the active register.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it'
    }).then((result) => {
      if (result.isConfirmed) delItem(row);
    });
  };

  /* ============================================================
   *  VIEW DETAILS (SweetAlert)
   * ============================================================ */

  const openDetails = (row) => {
    if (!row) return;

    const line = (label, value) =>
      `<div style="display:flex; gap:8px; padding:3px 0;">
         <div style="min-width:150px; color:#6c757d; font-size:12px;">${label}</div>
         <div style="font-size:13px; color:#24292f; font-weight:600;">${value || 'N/A'}</div>
       </div>`;

    const section = (title) =>
      `<div style="margin:14px 0 6px; padding-bottom:4px; border-bottom:2px solid #405189;
                   color:#405189; font-weight:700; font-size:13px;">${title}</div>`;

    const content = `
      <div style="text-align:left;">
        ${section('MLC DETAILS')}
        ${line('MLC No.', row.mlc_no)}
        ${line('Date &amp; Time', formatDateTimeLocal(row.mlc_datetime || `${row.mlc_date || ''} ${row.mlc_time || ''}`))}
        ${line('Type of MLC', row.mlc_type)}
        ${line('Doctor', getDoctorNameById(row.mlc_doctor, row))}
        ${line('Department', getDepartmentNameById(row.mlc_department, row))}

        ${section('PATIENT DETAILS')}
        ${line('UHID', row.mlc_uhid)}
        ${line('Patient Name', `${row.mlc_patient_title || ''} ${row.mlc_patient_name || ''}`.trim())}
        ${line('Gender / Marital', `${row.mlc_patient_gender || ''} ${row.mlc_marital_status ? ' / ' + row.mlc_marital_status : ''}`)}
        ${line('Age', row.mlc_age)}
        ${line('S/D/W o', `${row.mlc_sdw_title || ''} ${row.mlc_attendant_name || ''}`.trim())}
        ${line('Mobile', row.mlc_mobile)}
        ${line('Address', `${row.mlc_address || ''}${row.mlc_city ? ', ' + row.mlc_city : ''}${row.mlc_state ? ', ' + row.mlc_state : ''}`)}
        ${line('Religion', row.mlc_religion)}
        ${line('Occupation', row.mlc_occupation)}
        ${line('Nature of Injury', row.mlc_nature_injury)}
        ${line('Type of Patient', row.mlc_patient_type)}

        ${section('LEGAL DETAILS')}
        ${line('Brought By (I)', `${row.mlc_brought_by1_name || ''}${row.mlc_brought_by1_contact ? ' - ' + row.mlc_brought_by1_contact : ''}`)}
        ${line('Brought By (II)', `${row.mlc_brought_by2_name || ''}${row.mlc_brought_by2_contact ? ' - ' + row.mlc_brought_by2_contact : ''}`)}
        ${line('Case DD / FIR No.', row.mlc_fir_no)}
        ${line('FIR Date', formatDateLocal(row.mlc_fir_date))}
        ${line('Inves. Officer', row.mlc_inves_officer)}
        ${line('Batch No.', row.mlc_batch_no)}
        ${line('Police Station', row.mlc_police_station)}
        ${line('Identification Marks', row.mlc_identification_marks)}
      </div>
    `;

    Swal.fire({
      title: 'MLC Details',
      html: content,
      width: 760,
      showCloseButton: true,
      focusConfirm: false,
      confirmButtonText: 'Close'
    });
  };

  /* ============================================================
   *  RECEIPT PDF
   * ============================================================ */

  const printMlcReceipt = async (row) => {
    if (!row) return;

    const doc = new jsPDF('p', 'pt', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 40;
    let y = 0;

    // ===== HEADER =====
    const headerImageBase64 = await imageUrlToBase64(headerLogo);
    if (headerImageBase64) {
      doc.addImage(headerImageBase64, 'PNG', 0, 0, pageWidth, 80);
      y = 95;
    } else {
      y = 50;
    }

    // ===== TITLE =====
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setDrawColor(0);
    doc.rect(margin, y, pageWidth - margin * 2, 25);
    doc.text("MEDICO LEGAL CASE (MLC) REGISTRATION", pageWidth / 2, y + 17, { align: 'center' });
    y += 42;

    // ===== TWO-COLUMN DETAIL PRINTER =====
    doc.setFontSize(10);
    const leftX = margin + 6;
    const rightX = pageWidth / 2 + 20;
    const labelW = 100;

    const printLine = (label, value, x, yPos) => {
      doc.setFont('helvetica', 'normal');
      doc.text(String(label), x, yPos);
      doc.text(":", x + labelW, yPos);
      doc.setFont('helvetica', 'bold');
      const txt = doc.splitTextToSize(String(value || '-'), (pageWidth / 2) - labelW - 40);
      doc.text(txt, x + labelW + 8, yPos);
      doc.setFont('helvetica', 'normal');
      return (txt.length - 1) * 11;
    };

    const sectionHeader = (title, yPos) => {
      doc.setFillColor(241, 245, 251);
      doc.rect(margin, yPos - 11, pageWidth - margin * 2, 17, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(64, 81, 137);
      doc.text(title, margin + 6, yPos);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      return yPos + 20;
    };

    // ===== MLC DETAILS =====
    y = sectionHeader("MLC DETAILS", y);
    printLine("MLC No.", row.mlc_no, leftX, y);
    printLine("Date & Time", formatDateTimeLocal(row.mlc_datetime || `${row.mlc_date || ''} ${row.mlc_time || ''}`), rightX, y);
    y += 15;
    printLine("Type of MLC", row.mlc_type, leftX, y);
    printLine("Department", getDepartmentNameById(row.mlc_department, row), rightX, y);
    y += 15;
    printLine("Doctor", getDoctorNameById(row.mlc_doctor, row), leftX, y);
    printLine("Type of Patient", row.mlc_patient_type, rightX, y);
    y += 24;

    // ===== PATIENT DETAILS =====
    y = sectionHeader("PATIENT DETAILS", y);
    printLine("UHID", row.mlc_uhid, leftX, y);
    printLine("Patient Name", `${row.mlc_patient_title || ''} ${row.mlc_patient_name || ''}`.trim(), rightX, y);
    y += 15;
    printLine("Age", row.mlc_age, leftX, y);
    printLine("Gender", row.mlc_patient_gender, rightX, y);
    y += 15;
    printLine("Marital Status", row.mlc_marital_status, leftX, y);
    printLine("Mobile", row.mlc_mobile, rightX, y);
    y += 15;
    printLine("S/D/W o", `${row.mlc_sdw_title || ''} ${row.mlc_attendant_name || ''}`.trim(), leftX, y);
    printLine("Religion", row.mlc_religion, rightX, y);
    y += 15;
    printLine("Occupation", row.mlc_occupation, leftX, y);
    printLine("Nature of Injury", row.mlc_nature_injury, rightX, y);
    y += 15;
    const addrExtra = printLine(
      "Address",
      `${row.mlc_address || ''}${row.mlc_city ? ', ' + row.mlc_city : ''}${row.mlc_state ? ', ' + row.mlc_state : ''}`,
      leftX, y
    );
    printLine("Country", row.mlc_country, rightX, y);
    y += 15 + addrExtra + 9;

    // ===== LEGAL DETAILS =====
    y = sectionHeader("LEGAL DETAILS", y);

    autoTable(doc, {
      startY: y,
      head: [['Brought By', 'Name', 'Contact']],
      body: [
        ['I', row.mlc_brought_by1_name || '-', row.mlc_brought_by1_contact || '-'],
        ['II', row.mlc_brought_by2_name || '-', row.mlc_brought_by2_contact || '-'],
      ],
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 5, lineWidth: 0.5, lineColor: [0, 0, 0] },
      headStyles: { fillColor: [241, 245, 251], textColor: 0, fontStyle: 'bold' },
      columnStyles: { 0: { cellWidth: 70, halign: 'center' } },
      margin: { left: margin, right: margin }
    });

    y = doc.lastAutoTable.finalY + 18;

    printLine("Case DD/FIR No.", row.mlc_fir_no, leftX, y);
    printLine("FIR Date", formatDateLocal(row.mlc_fir_date), rightX, y);
    y += 15;
    printLine("Inves. Officer", row.mlc_inves_officer, leftX, y);
    printLine("Batch No.", row.mlc_batch_no, rightX, y);
    y += 15;
    const psExtra = printLine("Police Station", row.mlc_police_station, leftX, y);
    y += 15 + psExtra;
    const idExtra = printLine("Identification Marks", row.mlc_identification_marks, leftX, y);
    y += 15 + idExtra + 10;

    // ===== DECLARATION =====
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    const decl = doc.splitTextToSize(
      "This is a Medico Legal Case. Intimation has been given to the concerned police station as per statutory requirement. " +
      "This document is issued for official record and must be preserved as per hospital retention policy.",
      pageWidth - margin * 2
    );
    doc.text(decl, margin, y);
    y += decl.length * 10 + 40;

    // ===== SIGNATURES =====
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);

    doc.line(margin, y, margin + 150, y);
    doc.text("Duty Doctor", margin, y + 14);

    doc.line(pageWidth - margin - 180, y, pageWidth - margin, y);
    doc.text("Authorized Signature", pageWidth - margin, y + 14, { align: 'right' });

    // footer meta
    doc.setFontSize(7);
    doc.setTextColor(120);
    doc.text(
      `Printed on: ${new Date().toLocaleString('en-IN')}   |   Registered by: ${row.mlc_user_name || '-'}`,
      margin, doc.internal.pageSize.getHeight() - 25
    );

    window.open(doc.output('bloburl'), '_blank');
  };

  /* ============================================================
   *  TABLE STYLES + COLUMNS
   * ============================================================ */

  const hoverStyle = `
    .view-btn { visibility: hidden; transition: visibility 0.05s ease-in-out, opacity 0.15s; opacity: 0; }
    .rdt_TableRow:hover .view-btn { visibility: visible; opacity: 1; }
    .view-btn { pointer-events: auto; }
  `;

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: '#f1f5fb',
        color: '#405189',
        fontSize: '15px',
        fontWeight: '700',
        paddingLeft: '14px',
        paddingRight: '14px',
      }
    },
    rows: { style: { fontSize: '16px', minHeight: '58px' } },
    cells: { style: { paddingLeft: '14px', paddingRight: '14px', fontSize: '15px', lineHeight: '20px' } },
  };

  const columns = [
    {
      name: 'Action',
      cell: row => (
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-warning"
            title="Edit"
            onClick={() => editRow(row)}
          >
            <i className="ri-pencil-fill fs-16"></i>
          </button>

          <button
            type="button"
            className="btn btn-sm btn-outline-success"
            title="Print Receipt"
            onClick={() => printMlcReceipt(row)}
          >
            <i className="ri-printer-line fs-16"></i>
          </button>

          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            style={{ opacity: isAdmin ? 1 : 0.5, cursor: isAdmin ? 'pointer' : 'not-allowed' }}
            title="Delete"
            onClick={() => {
              if (!isAdmin) {
                Swal.fire({ icon: 'info', title: "Can't Delete", text: 'Only admins can delete an MLC record.' });
                return;
              }
              confirmDelete(row);
            }}
          >
            <i className="ri-delete-bin-5-fill fs-16"></i>
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '190px'
    },
    {
      name: '',
      cell: row => (
        <button
          className="btn btn-sm btn-primary view-btn"
          onClick={(e) => { e.stopPropagation(); openDetails(row); }}
          title="View"
          style={{ minWidth: 64 }}
        >
          View
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '90px'
    },
    {
      name: 'SN',
      selector: row => filteredList.findIndex(i => i.mlc_id === row.mlc_id) + 1,
      sortable: true,
      width: '80px'
    },
    {
      name: 'MLC No.',
      selector: row => row.mlc_no || '',
      sortable: true,
      width: '150px'
    },
    {
      name: 'Date & Time',
      selector: row => formatDateTimeLocal(row.mlc_datetime || `${row.mlc_date || ''} ${row.mlc_time || ''}`),
      sortable: true,
      width: '180px'
    },
    {
      name: 'UHID',
      selector: row => row.mlc_uhid || '',
      sortable: true,
      width: '140px'
    },
    {
      name: 'Patient Name',
      selector: row => `${row.mlc_patient_title || ''} ${row.mlc_patient_name || ''}`.trim(),
      sortable: true,
      width: '200px'
    },
    {
      name: 'Age / Gender',
      selector: row => `${row.mlc_age || '-'} / ${row.mlc_patient_gender || '-'}`,
      sortable: true,
      width: '170px'
    },
    {
      name: 'Mobile',
      selector: row => row.mlc_mobile || '',
      sortable: true,
      width: '140px'
    },
    {
      name: 'Type of MLC',
      selector: row => row.mlc_type || '',
      sortable: true,
      width: '190px'
    },
    {
      name: 'Nature of Injury',
      selector: row => row.mlc_nature_injury || '',
      sortable: true,
      width: '180px'
    },
    {
      name: 'Doctor',
      selector: row => getDoctorNameById(row.mlc_doctor, row),
      sortable: true,
      width: '170px'
    },
    {
      name: 'Department',
      selector: row => getDepartmentNameById(row.mlc_department, row),
      sortable: true,
      width: '170px'
    },
    {
      name: 'FIR No.',
      selector: row => row.mlc_fir_no || '',
      sortable: true,
      width: '140px'
    },
    {
      name: 'Police Station',
      selector: row => row.mlc_police_station || '',
      sortable: true,
      width: '200px'
    },
  ];

  /* ============================================================
   *  RENDER
   * ============================================================ */

  return (
    <div className="page-content">
      <style>{hoverStyle}</style>

      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-12">
            <div className="card" id="mlcList">

              {/* ===== HEADER ===== */}
              <div className="card-header border-0">
                <div className="row align-items-center gy-3">
                  <div className="col-sm">
                    <h2 style={{ color: "#405189" }}>MLC Registrations</h2>
                  </div>
                  <div className="col-sm-auto">
                    <div className="d-flex gap-1 flex-wrap">
                      <button
                        type="button"
                        className="btn btn-success add-btn"
                        onClick={addRegistration}
                      >
                        <i className="ri-add-line align-bottom me-1"></i> Add Registration
                      </button>
                      <button
                        type="button"
                        className="btn btn-soft-primary"
                        title="Refresh"
                        onClick={() => initData()}
                      >
                        <i className="ri-refresh-line"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* ===== FILTERS ===== */}
              <div className="card-body border border-dashed border-end-0 border-start-0">
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="row g-3">
                    <div className="col-md-2">
                      <label className="form-label">From</label>
                      <input
                        className="form-control"
                        type="date"
                        name="from"
                        value={formData.from}
                        onChange={(e) => updateFilters({ from: e.target.value })}
                      />
                    </div>

                    <div className="col-md-2">
                      <label className="form-label">To</label>
                      <input
                        className="form-control"
                        type="date"
                        name="to"
                        value={formData.to}
                        onChange={(e) => updateFilters({ to: e.target.value })}
                      />
                    </div>

                    <div className="col-md-2 d-flex align-items-end">
                      <button type="button" onClick={clearFilters} className="btn btn-info">
                        <i className="ri-filter-off-line me-1"></i> Clear
                      </button>
                    </div>

                    <div className="col-md-3"></div>

                    <div className="col-md-3" style={{ textAlign: "right" }}>
                      <label className="form-label"></label>
                      <div className="search-box">
                        <input
                          className="form-control search"
                          placeholder="Search patient name / MLC no / UHID..."
                          type="text"
                          name="search_key"
                          value={formData.search_key}
                          onChange={(e) => updateFilters({ search_key: e.target.value })}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              <br />

              {/* ===== TABLE ===== */}
              <div className="card-body pt-0">
                <div className="table-responsive table-card mb-1">
                  <DataTable
                    columns={columns}
                    data={filteredList}
                    progressPending={loading}
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    highlightOnHover
                    responsive
                    persistTableHead
                    customStyles={customStyles}
                    noHeader
                    noDataComponent={
                      <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>
                        <i className="ri-file-list-3-line" style={{ fontSize: '40px', color: '#ccc' }}></i>
                        <p style={{ marginTop: '10px' }}>No MLC records found for the selected filters</p>
                      </div>
                    }
                  />
                </div>

                <div className="d-flex justify-content-between align-items-center mt-2">
                  <div className="text-muted">
                    <strong>Total MLC Records:</strong> {filteredList.length}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MlcRegistrationList;